import { ComponentProps, MouseEventHandler, ReactNode, useMemo } from 'react';
import clsx from 'clsx';

import Icon from '../Icon/Icon';
import { type IconName } from '../Icon/types';

import styles from './SocialLinks.module.css';

const SupportedSocials: IconName[] = ['facebook', 'instagram', 'linkedIn', 'youtube', 'twitter', 'x', 'link'];

interface Props extends ComponentProps<'div'> {
  links?: Record<string, string | MouseEventHandler<HTMLAnchorElement>>;
  label?: ReactNode;
  variant?: 'light' | 'circles';
  utmSource?: string;
  utmMedium?: string;
}

function quickMergeUrl(url: string, params?: Record<string, string>) {
  if (url.includes('?') || !params) return url;

  return `${url}?${new URLSearchParams(params)}`;
}

export default function SocialLinks({
  links,
  label,
  variant = 'circles',
  className,
  utmSource,
  utmMedium,
  ...props
}: Props) {
  const socialLinks = useMemo<ReactNode[]>(
    () =>
      links
        ? Object.entries(links)
            .map(([name, urlOrFn]) => {
              if (!SupportedSocials.includes(name as IconName)) {
                return;
              }

              return (
                <a
                  key={name}
                  className={styles.socialLink}
                  href={
                    typeof urlOrFn === 'string'
                      ? quickMergeUrl(
                          urlOrFn,
                          utmSource ? { utm_source: utmSource, utm_medium: utmMedium || 'social' } : undefined,
                        )
                      : '#'
                  }
                  onClick={typeof urlOrFn === 'function' ? urlOrFn : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  suppressHydrationWarning
                >
                  <Icon
                    name={name as IconName}
                    aria-label={name}
                  />
                </a>
              );
            })
            .filter(Boolean)
        : [],
    [links, utmSource, utmMedium],
  );

  if (socialLinks.length <= 0) return null;

  return (
    <div
      className={clsx(styles.socialsGroup, styles[variant], className)}
      {...props}
    >
      {label && <span className={styles.socialsLabel}>{label}</span>}
      <nav
        className={styles.socials}
        aria-label="Social media"
      >
        {socialLinks}
      </nav>
    </div>
  );
}
